import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useEndpoints } from "../../../api/ApiContext";
import { ApiError, PASSWORD_POLICY_MESSAGE } from "../../../api/errors";
import type { AcceptInvitationResponse } from "../../../api/types";
import { AuthLayout } from "../../../auth/AuthLayout";
import { NewPasswordField } from "../../../auth/NewPasswordField";
import { Banner, Button, ButtonLink, Input } from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { checkPasswordLocally } from "../../../lib/passwordPolicy";
import { ROLE_LABEL } from "./orgCopy";

// Accept an organization invitation (G-06; UX-DESIGN.md §4.2 "Invite a
// member"): POST /v1/org/invitations/accept {token, password,
// recovery_contact} (org.go:49, 280-331), pre-authentication — the
// token is the credential.
//
// Token link. The portal route is /org/invitations/accept#token=<token>;
// the notifier in this build (internal/auth/org_notify.go:79) sends
// <portal>/invite#token=<token>, so /invite renders this page too. The
// token is read ONLY from the fragment, which browsers never send to a
// server: no access log, no proxy log, no Referer. Unlike the recovery
// links, a `?token=` is not used: a token that travelled in a query
// string has already been sent to (and possibly logged by) the server
// that served this page, so the user is asked for a fresh invitation.
// Either way the address bar is cleaned on arrival.
//
// Password: NIST SP 800-63B rev. 4 as the signup and recovery screens
// check it (src/lib/passwordPolicy.ts); the server re-checks it against
// the invitation's email (org.go:302-309, 400 password_policy {reason}).

function fragmentToken(hash: string): string | null {
  return new URLSearchParams(hash.replace(/^#/, "")).get("token") || null;
}

function queryHasToken(search: string): boolean {
  return new URLSearchParams(search).has("token");
}

/** Reads the fragment token once into memory and strips both the fragment and any query token from the URL. */
function useInvitationToken(): { token: string | null; leakedInQuery: boolean } {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState<string | null>(() => fragmentToken(location.hash));
  const [leakedInQuery] = useState<boolean>(() => queryHasToken(location.search));
  const dirty = location.hash !== "" || queryHasToken(location.search);

  useEffect(() => {
    if (dirty) navigate(location.pathname, { replace: true });
  }, [dirty, location.pathname, navigate]);

  return { token, leakedInQuery };
}

export function InvitationAcceptPage() {
  const ep = useEndpoints();
  const { token, leakedInQuery } = useInvitationToken();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [recoveryContact, setRecoveryContact] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<AcceptInvitationResponse | null>(null);

  if (!token) {
    return (
      <AuthLayout title="Accept your invitation" footer={<Link to="/login">Go to sign in</Link>}>
        <Banner tone="warning" title={leakedInQuery ? "This link isn't safe to use" : "The invitation link is incomplete"}>
          {leakedInQuery
            ? "This invitation link carried its code in a form that servers can record, so it can't be used. Ask the owner or admin who invited you to send a new invitation."
            : "Open the link from your invitation email exactly as it was sent. If it has expired (invitations last 7 days), ask the owner or admin who invited you to send a new one."}
        </Banner>
      </AuthLayout>
    );
  }

  if (invalid) {
    return (
      <AuthLayout title="Invitation not valid" footer={<Link to="/login">Go to sign in</Link>}>
        <Banner tone="error" title="This invitation can't be used">
          It may have expired (invitations last 7 days), been accepted already, or been revoked. Ask the owner or admin who invited you to
          send a new one.
        </Banner>
      </AuthLayout>
    );
  }

  if (done) {
    const p = done.pending_role_request;
    return (
      <AuthLayout title="You've joined">
        <div className="stack">
          <Banner tone="success" title="Invitation accepted" live="polite">
            {done.email} is now a member with the {ROLE_LABEL[done.role]} role.
          </Banner>
          {p ? (
            <Banner tone="info" title="Your invited role needs approval">
              The role you were invited with is privileged, so a second owner or admin has to approve it. It takes effect no earlier than{" "}
              {formatLocal(p.effective_at)}
              {p.single_user_fallback ? " (extended verification: nobody else can approve, so a longer waiting period applies)" : ""}. Until
              then you have the Member role.
            </Banner>
          ) : null}
          <p className="small">Sign in with your email and new password. You'll be asked to set up two-step verification.</p>
          <ButtonLink to="/login" variant="primary">
            Sign in
          </ButtonLink>
        </div>
      </AuthLayout>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const fe: typeof errors = {};
    const local = checkPasswordLocally(password);
    if (local) fe.password = PASSWORD_POLICY_MESSAGE[local];
    if (!fe.password && confirm !== password) fe.confirm = "The passwords don't match.";
    setErrors(fe);
    if (fe.password || fe.confirm || !token) return;
    setBusy(true);
    setError(null);
    try {
      setDone(await ep.org.acceptInvitation({ token, password, recovery_contact: recoveryContact.trim() }));
    } catch (err) {
      if (err instanceof ApiError && err.code === "invitation_invalid") setInvalid(true);
      else if (err instanceof ApiError && err.code === "password_policy") setErrors({ password: err.message });
      else setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Accept your invitation" footer={<Link to="/login">Already have an account? Sign in</Link>}>
      <form className="stack" onSubmit={submit} noValidate>
        <p>Choose a password to join the organization that invited you. You'll sign in with the email address the invitation was sent to.</p>
        <NewPasswordField value={password} onChange={setPassword} error={errors.password} />
        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={errors.confirm}
        />
        <Input
          label="Recovery contact"
          optional
          value={recoveryContact}
          onChange={(e) => setRecoveryContact(e.target.value)}
          hint="An email or phone number other than your sign-in email. Used to verify it's you if you lose your device, and to alert you about security changes."
        />
        {error ? (
          <Banner tone="error" live="alert">
            {error}
          </Banner>
        ) : null}
        <Button type="submit" block loading={busy}>
          Accept invitation
        </Button>
      </form>
    </AuthLayout>
  );
}
